import React, { useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Typography, Tabs, Tab } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import ActivityDetail from 'components/TrailSafe/ActivityDetail/ActivityDetail';
import ActivityContacts from 'components/TrailSafe/ActivityContacts/ActivityContacts';
import ActivityComments from 'components/TrailSafe/Comments/ActivityComments';
import {
  useGetActivityByIdQuery,
  useCreateStaffActivityContactMutation,
  useGetStaffActivityContactsQuery,
  useCreateStaffActivityCommentMutation,
  useGetStaffActivityCommentsQuery,
} from 'app/api/backendApiSlice';
import {
  ContactMethod,
  ContactType,
  ContactPersonType,
} from 'constants/Constants';
import MLToast from 'components/common/Toast/Toast';
import getDuration from 'utils/getDuration';
import { getStatusStyle } from '../LiveActivities/statusStyles';

dayjs.extend(utc);

const TABS = ['Overview', 'Contacts', 'Comments'];

const NotifyButton = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2 rounded-full border border-[var(--ts-border)] bg-[var(--ts-surface)] px-4 py-2 text-sm font-medium hover:bg-[var(--ts-accent-light)]"
  >
    {icon}
    {label}
  </button>
);

const NotifyRow = ({ label, onEmail, onSms }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[var(--ts-accent-light)] px-4 py-3">
    <Typography className="!font-medium">{label}</Typography>
    <div className="flex gap-2">
      <NotifyButton
        icon={<EmailIcon fontSize="small" />}
        label="Email"
        onClick={onEmail}
      />
      <NotifyButton
        icon={<SmsIcon fontSize="small" />}
        label="SMS"
        onClick={onSms}
      />
    </div>
  </div>
);

const ActivityDashboard = ({ activityId, handleClose }) => {
  const { showToast } = MLToast();
  const [tab, setTab] = useState(0);


  const { data: activity, isLoading: isUserLoading } = useGetActivityByIdQuery(
    activityId,
    { skip: !activityId }
  );

  const {
    data: activityContacts,
    isLoading: isContactsLoading,
    refetch: refetchContacts,
  } = useGetStaffActivityContactsQuery(activityId, { skip: !activityId });

  const [createActivityContact] = useCreateStaffActivityContactMutation();
  const [createActivityComment] = useCreateStaffActivityCommentMutation();

  const {
    data: activityComments,
    isLoading: isCommentsLoading,
    refetch: refetchComments,
  } = useGetStaffActivityCommentsQuery(activityId, { skip: !activityId });

  const handleCreateContact = async ({
    contactPerson,
    contactMethod,
    contactType,
    triggerEmContID,
    contUserID,
    contEmContID,
  }) => {
    const res = await createActivityContact({
      activity_id: activityId,
      contact_trigger: 'Internal User',
      contact_person: contactPerson,
      contact_method: contactMethod,
      contact_type: contactType,
      trigger_emergency_contact_id: triggerEmContID,
      contact_user_id: contUserID,
      contact_emergency_contact_id: contEmContID,
      contact_text:
        'Placeholder text for message that should be sent to user/emergency contact',
    });
    if (res.data?.[0]?.is_success) {
      refetchContacts();
      showToast('Contact queued successfully!', 'success');
    } else {
      const errMsg = res.error?.data?.message;
      showToast(errMsg || 'Something went wrong!', 'error');
    }
  };

  const handleCreateComment = async ({ commentText }) => {
    const res = await createActivityComment({
      activity_id: activityId,
      comment_text: commentText,
    });
    if (res.data?.[0]?.is_success) {
      refetchComments();
      showToast('Comment added successfully!', 'success');
    } else {
      const errMsg = res.error?.data?.message;
      showToast(errMsg || 'Something went wrong!', 'error');
    }
  };

  if (isUserLoading || isContactsLoading || isCommentsLoading)
    return <div>Loading...</div>;
  if (!activity) return <div>Error loading user details</div>;

  const walker = activity[0];
  const activityDetail = walker.activity[0];
  const emergencyContact = walker.emergency_contacts[0];

  const now = dayjs();
  const duration = getDuration({
    start: dayjs.utc(activityDetail.ActivityStartDate),
    end: dayjs.utc(activityDetail.ActivityEndDate ?? now),
    expected: activityDetail.ExpectedEndDate,
  });
  const badgeStatus = duration.isOverdue
    ? duration.isLate
      ? 'warning'
      : 'error'
    : 'good';
  const status = getStatusStyle(badgeStatus);

  const notifyUser = (contactType, contactMethod) =>
    handleCreateContact({
      contactPerson: ContactPersonType.USER_CONTACT,
      contactMethod,
      contactType,
      triggerEmContID: null,
      contUserID: walker.user_id,
      contEmContID: null,
    });

  const notifyEmergencyContact = (contactType, contactMethod) =>
    handleCreateContact({
      contactPerson: ContactPersonType.EMERGENCY_CONTACT,
      contactMethod,
      contactType,
      triggerEmContID: null,
      contUserID: null,
      contEmContID: emergencyContact.EmergencyContactID,
    });

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className="px-6 pb-8 pt-6 text-white"
        style={{
          background: `linear-gradient(135deg, ${status.color}, var(--ts-primary-dark))`,
        }}
      >
        <div className="mx-auto flex max-w-6xl items-start justify-between">
          <div>
            <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">
              {status.label} · {duration.durationText}
            </span>
            <Typography variant="h3" className="!font-bold mt-2">
              {walker.full_name}
            </Typography>
            <Typography variant="body1" className="opacity-90">
              {activityDetail.TrailName}, {activityDetail.ParkName}
            </Typography>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
            aria-label="Close"
          >
            <CloseIcon sx={{ color: 'white' }} />
          </button>
        </div>
      </div>

      <div className="mx-auto -mt-5 w-full max-w-6xl px-6">
        <div className="rounded-2xl border border-[var(--ts-border)] bg-[var(--ts-surface)] shadow-md">
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: status.color } }}
          >
            {TABS.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl flex-1 overflow-y-auto px-6 py-5">
        {tab === 0 && (
          <div className="flex flex-col gap-5">
            <ActivityDetail activity={activityDetail} colourScheme="dashboard" />
            <div className="rounded-2xl border border-[var(--ts-border)] bg-[var(--ts-surface)] p-5">
              <Typography variant="h6" className="!font-bold mb-1">
                Walker
              </Typography>
              <Typography variant="body1">{walker.full_name}</Typography>
              <Typography variant="body2" className="text-[var(--Grey-70)]">
                {walker.email_address} · {walker.phone_number}
              </Typography>
              <div className="my-3 border-t border-[var(--ts-border)]" />
              <Typography variant="h6" className="!font-bold mb-1">
                Emergency Contact
              </Typography>
              <Typography variant="body1">
                {emergencyContact.ContactName} ({emergencyContact.Relationship})
              </Typography>
              <Typography variant="body2" className="text-[var(--Grey-70)]">
                {emergencyContact.ContactEmail} · {emergencyContact.ContactPhone}
              </Typography>
            </div>
            <div className="flex flex-col gap-2">
              <NotifyRow
                label="Send checkout reminder to walker"
                onEmail={() =>
                  notifyUser(ContactType.CHECKOUT_REMINDER, ContactMethod.EMAIL)
                }
                onSms={() =>
                  notifyUser(ContactType.CHECKOUT_REMINDER, ContactMethod.SMS)
                }
              />
              <NotifyRow
                label="Send alert to walker"
                onEmail={() =>
                  notifyUser(ContactType.CHECKOUT_ALERT, ContactMethod.EMAIL)
                }
                onSms={() =>
                  notifyUser(ContactType.CHECKOUT_ALERT, ContactMethod.SMS)
                }
              />
              <NotifyRow
                label="Send alert to emergency contact"
                onEmail={() =>
                  notifyEmergencyContact(
                    ContactType.EMERGENCY_CONTACT_ALERT,
                    ContactMethod.EMAIL
                  )
                }
                onSms={() =>
                  notifyEmergencyContact(
                    ContactType.EMERGENCY_CONTACT_ALERT,
                    ContactMethod.SMS
                  )
                }
              />
            </div>
          </div>
        )}
        {tab === 1 && (
          <ActivityContacts
            contacts={[...(activityContacts?.[0]?.contacts ?? [])]}
            colourScheme="dashboard"
            classList="h-full"
          />
        )}
        {tab === 2 && (
          <ActivityComments
            comments={[...(activityComments?.[0]?.comments ?? [])]}
            colourScheme="dashboard"
            classList="h-full"
            addCommentFunc={handleCreateComment}
          />
        )}
      </div>
    </div>
  );
};

export default ActivityDashboard;
